'use client'

import React, { memo } from 'react'
import { Button } from '../ui/button'
import ToggleSwitch from '../ui/toggle-switch'
import { SettingsState, PerformanceMetrics } from '@/types/settings'
import { cn } from '@/lib/utils'

interface PerformanceSettingsPanelProps {
  settings: SettingsState
  performanceMetrics: PerformanceMetrics
  onSettingChange: (key: keyof SettingsState, value: any) => void
  onResetSection: (section: string) => void
}

const PerformanceSettingsPanel = memo<PerformanceSettingsPanelProps>(({
  settings,
  performanceMetrics,
  onSettingChange,
  onResetSection
}) => {
  return (
    <div className="space-y-6">
      {/* PERFORMANCE METRICS */}
      <div className="p-4 bg-gradient-to-r from-slate-800/30 to-slate-700/30 rounded-lg border border-slate-600/30">
        <h4 className="text-sm font-medium text-slate-200 mb-3">System Performance</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-xs">
          <div className="text-center">
            <div className="text-cyan-400 font-mono text-lg">{performanceMetrics.renderTime}ms</div>
            <div className="text-slate-400">Render Time</div>
          </div>
          <div className="text-center">
            <div className="text-green-400 font-mono text-lg">{performanceMetrics.memoryUsage}MB</div>
            <div className="text-slate-400">Memory</div>
          </div>
          <div className="text-center">
            <div className="text-purple-400 font-mono text-lg">{performanceMetrics.fps}</div>
            <div className="text-slate-400">FPS</div>
          </div>
          <div className="text-center">
            <div className="text-orange-400 font-mono text-lg">{settings.maxWorkers}</div>
            <div className="text-slate-400">Workers</div>
          </div>
        </div>
        <div className="flex space-x-2 mt-3">
          <Button
            onClick={() => onResetSection('performance')}
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Quality Level - PROCESSING POWER */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200" htmlFor="quality">
          Quality Level <span className="text-orange-400 uppercase">({settings.quality})</span>
        </label>
        <select
          id="quality"
          value={settings.quality}
          onChange={(e) => onSettingChange('quality', e.target.value)}
          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-slate-200 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30"
          aria-label="Select quality level"
        >
          <option value="low">🐌 Low (60% Performance, Basic Quality)</option>
          <option value="medium">⚖️ Medium (80% Performance, Good Quality)</option>
          <option value="high">🚀 High (90% Performance, Excellent Quality)</option>
          <option value="ultra">💎 Ultra (Maximum Quality, Full Power)</option>
        </select>
      </div>

      {/* Memory Limit */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200" htmlFor="memoryLimit">
          Memory Limit: <span className="text-red-400">{settings.memoryLimit}MB</span>
        </label>
        <input
          id="memoryLimit"
          type="range"
          min="128"
          max="2048"
          step="128"
          value={settings.memoryLimit}
          onChange={(e) => onSettingChange('memoryLimit', parseInt(e.target.value))}
          className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
          aria-label="Adjust memory limit"
        />
        <div className="flex justify-between text-xs text-slate-400">
          <span>128MB (Minimal)</span>
          <span>2048MB (Maximum)</span>
        </div>
      </div>

      {/* Max Workers - PARALLEL PROCESSING POWER */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200" htmlFor="maxWorkers">
          Max Workers: <span className="text-blue-400">{settings.maxWorkers} threads</span>
        </label>
        <input
          id="maxWorkers"
          type="range"
          min="1"
          max="16"
          step="1"
          value={settings.maxWorkers}
          onChange={(e) => onSettingChange('maxWorkers', parseInt(e.target.value))}
          className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
          aria-label="Adjust maximum number of worker threads"
        />
        <div className="flex justify-between text-xs text-slate-400">
          <span>1 (Single Core)</span>
          <span>16 (Maximum Parallel)</span>
        </div>
      </div>

      {/* POWER TOGGLES */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Caching Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Smart Caching</h4>
            <p className="text-xs text-slate-400">Intelligent data caching</p>
          </div>
          <ToggleSwitch
            value={settings.enableCaching}
            onChange={(value) => onSettingChange('enableCaching', value)}
            activeColor="cyan"
            title="Toggle smart caching"
            ariaLabel={(value) => value ? "Disable smart caching" : "Enable smart caching"}
          />
        </div>

        {/* Hardware Acceleration Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">GPU Acceleration</h4>
            <p className="text-xs text-slate-400">Hardware-accelerated processing</p>
          </div>
          <ToggleSwitch
            value={settings.enableHardwareAccel}
            onChange={(value) => onSettingChange('enableHardwareAccel', value)}
            activeColor="green"
            title="Toggle GPU acceleration"
            ariaLabel={(value) => value ? "Disable GPU acceleration" : "Enable GPU acceleration"}
          />
        </div>

        {/* Compression Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Data Compression</h4>
            <p className="text-xs text-slate-400">Compress audio data</p>
          </div>
          <ToggleSwitch
            value={settings.enableCompression}
            onChange={(value) => onSettingChange('enableCompression', value)}
            activeColor="purple"
            title="Toggle data compression"
            ariaLabel={(value) => value ? "Disable compression" : "Enable compression"}
          />
        </div>

        {/* Profiling Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Performance Profiling</h4>
            <p className="text-xs text-slate-400">Monitor system performance</p>
          </div>
          <ToggleSwitch
            value={settings.enableProfiling}
            onChange={(value) => onSettingChange('enableProfiling', value)}
            activeColor="orange"
            title="Toggle performance profiling"
            ariaLabel={(value) => value ? "Disable profiling" : "Enable profiling"}
          />
        </div>
      </div>
    </div>
  )
})

PerformanceSettingsPanel.displayName = 'PerformanceSettingsPanel'

export default PerformanceSettingsPanel